/**
 * File: TodayMoneyLine.tsx
 * Path: apps/admin/src/features/today/components/TodayMoneyLine.tsx
 * Description: Money on Today is one line: Owed to you, Still to bill and
 * the bank, with a link to the accounting Overview where the books live
 * (Liam, admin UI audit C2, 4 Nov 2026).
 */

import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ADMIN_PATHS, formatCents } from "@idk/config";
import { SummaryLine, type SummaryItem } from "@/shared/components/SummaryLine";
import { OWED_TO_YOU, STILL_TO_BILL } from "@/shared/utils/owedUtils";
import { TERM_HINTS } from "@/shared/utils/termHintsData";
import { useTodayMoneyController } from "@/features/today/hooks/useTodayMoneyController";

export const TodayMoneyLine: React.FC = () => {
  const { owed, toBill, bankCents } = useTodayMoneyController();
  const items: SummaryItem[] = [
    owed === "unavailable"
      ? { label: OWED_TO_YOU.label, value: "Not available", hint: OWED_TO_YOU.unavailable }
      : {
          label: OWED_TO_YOU.label,
          value: owed === null ? "…" : formatCents(owed.cents),
          chip: owed !== null && owed.lateCount > 0 ? { tone: "red", text: `${owed.lateCount} late` } : undefined,
          hint: OWED_TO_YOU.hint,
        },
    { label: STILL_TO_BILL.label, value: toBill === null ? "…" : formatCents(toBill.cents), hint: STILL_TO_BILL.hint },
  ];
  if (bankCents !== null) items.push({ label: "Bank", value: formatCents(bankCents), hint: TERM_HINTS.bankAndCash });
  return (
    <SummaryLine
      label="Money"
      items={items}
      trailing={
        <div>
          <Link
            to={ADMIN_PATHS.accounting.overview}
            className="inline-flex min-h-11 items-center gap-stack-xs text-sm font-bold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Overview
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      }
    />
  );
};
